import api from './api'

export interface Cost {
  id: number
  workshopCompanyId: number
  type: 'S' | 'P' // 'S' for Service, 'P' for Piece
  description: string
  hourlyRate?: number // For services only
  unitPrice?: number // For pieces only
  quantity?: number // Stock for pieces only
  active: boolean
  createdAt?: string
  updatedAt?: string
  createdBy?: number
  updatedBy?: number
  displayText?: string // For select boxes: "Description (€X/h)" or "Description (€X)"
}

export interface CostForWorkOrder {
  id: number
  workOrderId: number
  costId: number
  hours: number // Hours taken for this cost (for services) or quantity (for pieces)
  quantity?: number // For pieces, can override hours
  notes?: string
  createdAt?: string
  updatedAt?: string
  createdBy?: number
  updatedBy?: number
  cost?: Cost
  subtotal?: number
}

export interface CreateCostRequest {
  workshopCompanyId: number
  type: 'S' | 'P'
  description: string
  hourlyRate?: number
  unitPrice?: number
  quantity?: number // Stock for pieces only
  active?: boolean
  createdBy?: number
}

export interface CreateCostForWorkOrderRequest {
  costId: number
  hours: number
  quantity?: number
  notes?: string
  createdBy?: number
}

export const costService = {
  getByWorkshop: async (workshopCompanyId: number) => {
    const response = await api.get<{ success: boolean; data: Cost[] }>(
      `/costs/workshop/${workshopCompanyId}`
    )
    return response.data.data
  },

  getByWorkshopAndType: async (workshopCompanyId: number, type: 'S' | 'P') => {
    const response = await api.get<{ success: boolean; data: Cost[] }>(
      `/costs/workshop/${workshopCompanyId}/type/${type}`
    )
    return response.data.data
  },

  getById: async (id: number) => {
    const response = await api.get<{ success: boolean; data: Cost }>(`/costs/${id}`)
    return response.data.data
  },

  create: async (data: CreateCostRequest) => {
    const response = await api.post<{ success: boolean; data: Cost }>('/costs', data)
    return response.data.data
  },

  update: async (id: number, data: CreateCostRequest) => {
    const response = await api.put<{ success: boolean; data: Cost }>(`/costs/${id}`, data)
    return response.data.data
  },

  delete: async (id: number) => {
    const response = await api.delete<{ success: boolean; data: null }>(`/costs/${id}`)
    return response.data.data
  },

  addStock: async (id: number, quantity: number) => {
    const response = await api.patch<{ success: boolean; data: Cost }>(`/costs/${id}/stock`, {
      quantity,
    })
    return response.data.data
  },

  // CostForWorkOrder methods
  getWorkOrderCosts: async (workOrderId: number) => {
    const response = await api.get<{ success: boolean; data: CostForWorkOrder[] }>(
      `/work-orders/${workOrderId}/costs`
    )
    return response.data.data
  },

  addCostToWorkOrder: async (workOrderId: number, data: CreateCostForWorkOrderRequest) => {
    const response = await api.post<{ success: boolean; data: CostForWorkOrder }>(
      `/work-orders/${workOrderId}/costs`,
      data
    )
    return response.data.data
  },

  updateWorkOrderCost: async (workOrderId: number, costId: number, data: CreateCostForWorkOrderRequest) => {
    const response = await api.put<{ success: boolean; data: CostForWorkOrder }>(
      `/work-orders/${workOrderId}/costs/${costId}`,
      data
    )
    return response.data.data
  },

  removeCostFromWorkOrder: async (workOrderId: number, costId: number) => {
    const response = await api.delete<{ success: boolean; data: null }>(
      `/work-orders/${workOrderId}/costs/${costId}`
    )
    return response.data.data
  },
}

